/**
 * Builds the markdown prompt sent to Ivy when the creator dispatches accepted reviewer comments.
 *
 * Output format:
 *   - Human-readable markdown grouped by anchor (slide+timecode or question), each
 *     with the reviewer's text + the creator's optional note
 *   - Trailing HTML comment with structured JSON for downstream agents
 *
 * Anchor-agnostic: works for both video (slide) and assessment (question) reviews.
 */

import type { QueuedReviewItem } from '../stores/reviewQueueStore';
import { anchorBulletPrefix, anchorGroupKey, anchorPromptHeading } from './reviewAnchor';

export interface BuildReviewPromptOptions {
  /** Optional free-form preamble the creator typed in the composer. */
  creatorPreamble?: string;
}

export function buildReviewPrompt(items: QueuedReviewItem[], options: BuildReviewPromptOptions = {}): string {
  if (items.length === 0) return options.creatorPreamble?.trim() ?? '';

  const lines: string[] = [];

  if (options.creatorPreamble?.trim()) {
    lines.push(options.creatorPreamble.trim());
    lines.push('');
  }

  const reviewers = Array.from(new Set(items.map((i) => i.reviewerName)));
  const reviewerLabel = reviewers.length === 1 ? reviewers[0] : `${reviewers.length} reviewers`;

  // Questions vs slides: a uniform anchor kind lets us word the lead-in correctly.
  const isAssessment = items.every((i) => i.anchor.kind === 'question');
  const subject = isAssessment ? 'this assessment' : 'this video';
  lines.push(`I've reviewed feedback from ${reviewerLabel} on ${subject}. Please address these changes:`);
  lines.push('');

  // Group by anchor target for legibility (all comments on one slide/question together).
  const groups = new Map<string, QueuedReviewItem[]>();
  for (const item of items) {
    const key = anchorGroupKey(item.anchor);
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
  }

  for (const [, groupItems] of groups) {
    lines.push(anchorPromptHeading(groupItems[0].anchor));
    for (const item of groupItems) {
      const note = item.creatorNote.trim();
      const original = item.text.trim();
      lines.push(`- ${anchorBulletPrefix(item.anchor)}${item.reviewerName}: "${original}"`);
      if (note && note !== original) {
        lines.push(`  - My note: ${note}`);
      }
    }
    lines.push('');
  }

  if (isAssessment) {
    lines.push(
      '_To apply: edit the relevant question in plan.json (and for interactive questions its `questions/{qId}/{qId}.html`) via safe_edit_file, then run sync_workspace + harvest_assessment_manifest so the studio refreshes._',
    );
    lines.push('');
  }

  // Structured trailer for downstream agents (invisible in most renders).
  const meta = items.map((i) => ({
    reviewId: i.reviewId,
    commentId: i.commentId,
    anchor: i.anchor,
    reviewer: i.reviewerName,
    reviewText: i.text,
    creatorNote: i.creatorNote,
  }));
  lines.push(`<!-- review-context ${JSON.stringify({ reviewItems: meta })} -->`);

  return lines.join('\n');
}
